import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { WorkerLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import OnboardingSlides from "../components/OnboardingSlides";
import {
  Play, CheckCircle2, RotateCcw, Timer, ArrowLeft,
  Circle, CheckSquare, Square, Sparkles, AlertCircle, ShieldCheck
} from "lucide-react";

const statusConfig = {
  pending: { label: "Pending", color: "bg-[#21362A] text-[#A1B4A8]", icon: Circle },
  in_progress: { label: "In Progress", color: "bg-[#097969]/20 text-[#2A9D8F]", icon: Timer },
  completed: { label: "Completed", color: "bg-[#D4AF37]/20 text-[#D4AF37]", icon: CheckCircle2 },
  confirmed: { label: "Confirmed", color: "bg-[#2A9D8F]/20 text-[#2A9D8F]", icon: ShieldCheck },
};

export default function WorkerTasks() {
  const navigate = useNavigate();
  const [tasks, setTasks] = useState([]);
  const [dailyLimit, setDailyLimit] = useState(10);
  const [allConfirmed, setAllConfirmed] = useState(false);
  const [onboardingSkipped, setOnboardingSkipped] = useState(true);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState({});
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const hasDecidedOnboarding = useRef(false);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const r = await api.get("/worker/tasks/today");
      const data = r.data;
      setTasks(data.progress?.tasks || []);
      setDailyLimit(data.daily_limit || 10);
      setAllConfirmed(data.progress?.all_confirmed || false);
      setOnboardingSkipped(data.progress?.onboarding_skipped || false);
      if (!data.progress?.onboarding_skipped && !hasDecidedOnboarding.current) {
        hasDecidedOnboarding.current = true;
        setShowOnboarding(true);
      }
    } catch (e) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const total = tasks.length;
  const completedCount = tasks.filter(t => t.status === "completed").length;
  const confirmedCount = tasks.filter(t => t.status === "confirmed").length;
  const inProgressCount = tasks.filter(t => t.status === "in_progress").length;
  const progressPct = total > 0 ? Math.round(((completedCount + confirmedCount) / total) * 100) : 0;

  const handleStart = async (taskId) => {
    setProcessing(p => ({ ...p, [taskId]: "start" }));
    try {
      await api.post(`/worker/tasks/${taskId}/start`);
      toast.success("Task started");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Start failed");
    } finally {
      setProcessing(p => ({ ...p, [taskId]: null }));
    }
  };

  const handleComplete = async (taskId) => {
    setProcessing(p => ({ ...p, [taskId]: "complete" }));
    try {
      await api.post(`/worker/tasks/${taskId}/complete`);
      toast.success("Task completed");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Complete failed");
    } finally {
      setProcessing(p => ({ ...p, [taskId]: null }));
    }
  };

  const handleConfirm = async (taskId) => {
    setProcessing(p => ({ ...p, [taskId]: "confirm" }));
    try {
      const r = await api.post(`/worker/tasks/${taskId}/confirm`);
      toast.success("Task confirmed");
      if (r.data.all_confirmed) {
        toast.success("All tasks confirmed. Great work today!");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Confirm failed");
    } finally {
      setProcessing(p => ({ ...p, [taskId]: null }));
    }
  };

  const handleOnboardingClose = async (skipped) => {
    setShowOnboarding(false);
    try {
      await api.post("/worker/tasks/onboarding-skip", { skip: skipped });
      setOnboardingSkipped(true);
    } catch (e) {
      console.error("skip onboarding failed", e);
    }
  };

  const confirmAll = async () => {
    const toConfirm = tasks.filter(t => t.status === "completed");
    if (toConfirm.length === 0) return;
    for (const t of toConfirm) {
      try {
        await api.post(`/worker/tasks/${t.task_id}/confirm`);
      } catch (e) {
        toast.error(`Confirm failed for: ${t.title}`);
      }
    }
    toast.success("All completed tasks confirmed");
    setShowReviewModal(false);
    load();
  };

  if (loading) {
    return (
      <WorkerLayout>
        <div className="min-h-[60vh] flex items-center justify-center text-[#A1B4A8]">Loading tasks…</div>
      </WorkerLayout>
    );
  }

  return (
    <WorkerLayout>
      <OnboardingSlides open={showOnboarding} onClose={handleOnboardingClose} />

      <div className="space-y-8">
        <section className="flex items-end justify-between flex-wrap gap-4">
          <div>
            <div className="label-overline text-[#D4AF37]">Daily Focus</div>
            <h1 className="font-display text-4xl lg:text-5xl mt-2">Today&apos;s Tasks</h1>
          </div>
          <Button variant="outline" className="border-[#21362A] text-[#A1B4A8] hover:text-white" onClick={() => navigate("/worker")} data-testid="back-to-dashboard-btn">
            <ArrowLeft className="w-4 h-4 mr-2" /> Back to Studio
          </Button>
        </section>

        <section className="grid grid-cols-1 md:grid-cols-4 gap-6">
          <StatCard label="Total Tasks" value={`${total} / ${dailyLimit}`} data-testid="stat-total" />
          <StatCard label="In Progress" value={inProgressCount} data-testid="stat-in-progress" />
          <StatCard label="Completed" value={completedCount} data-testid="stat-completed" />
          <StatCard label="Confirmed" value={confirmedCount} data-testid="stat-confirmed" />
        </section>

        <Card className="bg-[#0C140F] border-[#21362A] rounded-sm">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="font-display text-xl">Progress</CardTitle>
              <span className="text-sm text-[#A1B4A8]">{progressPct}%</span>
            </div>
          </CardHeader>
          <CardContent>
            <Progress value={progressPct} className="h-2 bg-[#132018] [&>div]:bg-[#D4AF37]" data-testid="task-progress-bar" />
          </CardContent>
        </Card>

        {allConfirmed && (
          <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            className="rounded-sm border border-[#2A9D8F]/40 bg-[#2A9D8F]/10 p-4 text-sm text-[#2A9D8F] flex items-center gap-3"
            data-testid="all-confirmed-banner"
          >
            <ShieldCheck className="w-5 h-5" />
            <div>
              <strong>All tasks confirmed.</strong> You have completed and verified today&apos;s work. You may now proceed to product generation or other studio tasks.
            </div>
          </motion.div>
        )}

        <section className="space-y-4">
          <AnimatePresence>
            {tasks.map((task, idx) => {
              const cfg = statusConfig[task.status] || statusConfig.pending;
              const StatusIcon = cfg.icon;
              const isProcessing = processing[task.task_id];
              return (
                <motion.div
                  key={task.task_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: idx * 0.04 }}
                >
                  <Card className={`bg-[#0C140F] border rounded-sm transition-colors ${task.status === "confirmed" ? "border-[#2A9D8F]/30" : task.status === "completed" ? "border-[#D4AF37]/30" : task.status === "in_progress" ? "border-[#097969]/30" : "border-[#21362A]"}`}>
                    <CardContent className="p-5 flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1 space-y-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge className={`${cfg.color} text-xs rounded-sm`} data-testid={`task-badge-${task.task_id}`}>
                            <StatusIcon className="w-3 h-3 mr-1" /> {cfg.label}
                          </Badge>
                          {task.status === "in_progress" && task.started_at && (
                            <span className="text-xs text-[#A1B4A8]">Started {new Date(task.started_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</span>
                          )}
                        </div>
                        <h3 className="text-white font-display text-lg">{task.title}</h3>
                        <p className="text-sm text-[#A1B4A8]">{task.description}</p>
                      </div>

                      <div className="flex items-center gap-2 flex-wrap">
                        {task.status === "pending" && (
                          <Button
                            size="sm"
                            className="bg-[#097969] hover:bg-[#0a8a78] text-white"
                            onClick={() => handleStart(task.task_id)}
                            disabled={isProcessing}
                            data-testid={`task-start-${task.task_id}`}
                          >
                            <Play className="w-4 h-4 mr-2" /> {isProcessing ? "Starting…" : "Start"}
                          </Button>
                        )}
                        {task.status === "in_progress" && (
                          <Button
                            size="sm"
                            className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
                            onClick={() => handleComplete(task.task_id)}
                            disabled={isProcessing}
                            data-testid={`task-complete-${task.task_id}`}
                          >
                            <CheckCircle2 className="w-4 h-4 mr-2" /> {isProcessing ? "Completing…" : "Complete"}
                          </Button>
                        )}
                        {task.status === "completed" && (
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37]/10"
                            onClick={() => handleConfirm(task.task_id)}
                            disabled={isProcessing}
                            data-testid={`task-confirm-${task.task_id}`}
                          >
                            <ShieldCheck className="w-4 h-4 mr-2" /> {isProcessing ? "Confirming…" : "Confirm"}
                          </Button>
                        )}
                        {task.status === "confirmed" && (
                          <div className="flex items-center gap-2 text-[#2A9D8F]">
                            <CheckSquare className="w-5 h-5" />
                            <span className="text-sm font-medium">Verified</span>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </section>

        {completedCount > 0 && !allConfirmed && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="flex justify-center"
          >
            <Button
              size="lg"
              className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold"
              onClick={() => setShowReviewModal(true)}
              data-testid="review-confirm-all-btn"
            >
              <Sparkles className="w-5 h-5 mr-2" /> Review & Confirm All
            </Button>
          </motion.div>
        )}
      </div>

      {showReviewModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-sm" data-testid="review-modal">
          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} className="w-full max-w-lg px-4">
            <Card className="bg-[#0C140F] border-[#21362A] rounded-sm max-h-[80vh] overflow-y-auto">
              <CardHeader>
                <CardTitle className="font-display text-xl flex items-center gap-2">
                  <AlertCircle className="w-5 h-5 text-[#D4AF37]" /> Review Tasks
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <p className="text-sm text-[#A1B4A8]">Please review each completed task before confirming.</p>
                {tasks.filter(t => t.status === "completed").map(task => (
                  <div key={task.task_id} className="flex items-start gap-3 p-3 border border-[#21362A] rounded-sm bg-[#050A07]">
                    <Square className="w-5 h-5 text-[#D4AF37] flex-shrink-0 mt-0.5" />
                    <div>
                      <div className="text-white text-sm font-medium">{task.title}</div>
                      <div className="text-xs text-[#A1B4A8]">{task.description}</div>
                    </div>
                  </div>
                ))}
                <div className="flex gap-3 pt-2">
                  <Button variant="outline" className="border-[#21362A] text-[#A1B4A8]" onClick={() => setShowReviewModal(false)}>Cancel</Button>
                  <Button className="bg-[#D4AF37] hover:bg-[#F0C84A] text-[#050A07] font-semibold flex-1" onClick={confirmAll} data-testid="confirm-all-final-btn">
                    <ShieldCheck className="w-4 h-4 mr-2" /> Confirm All Correct
                  </Button>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        </div>
      )}
    </WorkerLayout>
  );
}

function StatCard({ label, value, ...props }) {
  return (
    <Card className="bg-[#0C140F] border-[#21362A] rounded-sm" {...props}>
      <CardContent className="p-5">
        <div className="label-overline text-xs text-[#A1B4A8]">{label}</div>
        <div className="font-display text-3xl mt-2 text-white">{value}</div>
      </CardContent>
    </Card>
  );
}
